import React, { useEffect, useMemo } from 'react';

import type { Location } from 'history';
import Cookie from 'js-cookie';
import { connect } from 'react-redux';
import { matchPath, Redirect, Route, Switch } from 'react-router-dom';
import { AnyAction, Dispatch } from 'redux';
import {
  saveUnbounceParamToLocalforage,
  UnbounceParam,
} from 'registration/helpers/experiment/xena';

import { clearErrorDialog } from 'shared/actions/errorActions';
import ErrorBoundary from 'shared/components/ErrorBoundary';
import ErrorDialog from 'shared/components/ErrorDialog';
import activateABTest, {
  getRegVariantChar,
} from 'shared/helpers/activateABTest';
import { parsePageQuery } from 'shared/helpers/query';
import Sentry from 'shared/helpers/sentry';
import { registerSignupIntent } from 'shared/helpers/tracking';
import withGATracker from 'shared/helpers/withGATracker';

import { NurseProfile } from 'talent/types';

import getGoogleClickID from '../helpers/getGoogleClickID';
import { getPreviewTrackingParams } from '../helpers/getPreviewTrackingParams';
import TalentOnboardContainerAmbient from './TalentOnboardContainerAmbient';
import TalentOnboardContainerQuicksilver from './TalentOnboardContainerQuicksilver';

type LocationState = {
  isLiteOnboardingFlow?: boolean;
};

type TalentOnboardContainerProps = {
  dispatch: Dispatch<AnyAction>;
  location: Location<LocationState>;
  match: {
    url: string;
  };
  talentProfile?: Partial<NurseProfile>;
  uiState: Map<string, any>;
};

const TalentOnboardContainer = ({
  dispatch,
  location,
  match,
  talentProfile = {},
  uiState,
}: TalentOnboardContainerProps) => {
  const regVariant = useMemo(() => {
    if (
      talentProfile &&
      talentProfile.onboard_variant &&
      (talentProfile.signup_intent === 'hiring' ||
        location.state?.isLiteOnboardingFlow)
    ) {
      const { onboard_variant: onboardVariant } = talentProfile;
      // Force the `onboardVariant` if it exists, assign one if it's null
      return activateABTest('onboarding', {
        variantOverride: onboardVariant,
        legacyCalculation: true,
      });
    }
    return activateABTest('onboarding', {
      legacyCalculation: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    Sentry.setTag('feature.onboarding', true);
    Sentry.setTag('feature.onboarding_variant', regVariant);
    const replay = Sentry.getReplay();
    // The replay integration should be loaded, but in case it isn't, we'll
    // explicitly check to avoid crashing the app.
    if (replay) {
      replay.startBuffering();
    } else {
      console.error('Sentry replay integration not loaded');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const query = parsePageQuery(location.search);

    const gclid = getGoogleClickID(query);
    if (gclid) {
      Cookie.set('gclid', gclid, { expires: 90 });
    }

    if (talentProfile && talentProfile.signup_intent) {
      registerSignupIntent(talentProfile.signup_intent);
    }

    const mpOptions = getPreviewTrackingParams(
      query.preview_checked,
      query.preview_enrolled
    ) as any;

    const mpVariant = query.mp_variant;
    if (mpVariant) {
      mpOptions.variant = mpVariant;
    }
    mpOptions['Onboard Variant'] = regVariant;
    mixpanel.track('Sign Up Visited', mpOptions);

    saveUnbounceParamToLocalforage(UnbounceParam.desired_location, query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeErrorDialog = () => {
    dispatch(clearErrorDialog());
  };

  const funnelMatch = matchPath(window.location.pathname, {
    path: `${match.url}/:funnelChar`,
  });
  const funnelChar = funnelMatch ? funnelMatch.params.funnelChar : '';
  const regVariantChar = getRegVariantChar(regVariant);

  if (regVariantChar !== funnelChar) {
    if (funnelChar === '_') {
      // From the disambiguation page, we want to correctly set the variant
      // but skip the first step.
      const disambiguationFunnelMatch = matchPath(window.location.pathname, {
        path: `${match.url}/_/:stepNumber`,
      });
      const stepNumber = disambiguationFunnelMatch
        ? disambiguationFunnelMatch.params.stepNumber
        : '';
      return (
        // Preserve the query string during the redirect
        <Redirect
          to={{
            ...location,
            pathname: `${match.url}/${regVariantChar}/${stepNumber}`,
          }}
        />
      );
    }
    return (
      // Preserve the query string during the redirect
      <Redirect
        to={{ ...location, pathname: `${match.url}/${regVariantChar}` }}
      />
    );
  }

  let errorDialog = null;
  const errorDialogMsg = uiState.get('errorDialogMessage');
  if (errorDialogMsg) {
    errorDialog = (
      <ErrorDialog message={errorDialogMsg} onClose={closeErrorDialog} />
    );
  }

  return (
    <ErrorBoundary>
      <div>
        <Switch>
          <Route
            component={TalentOnboardContainerQuicksilver}
            path={`${match.url}/y`}
          />
          <Route
            component={TalentOnboardContainerQuicksilver}
            path={`${match.url}/z`}
          />
          <Route
            component={TalentOnboardContainerAmbient}
            path={`${match.url}/a`}
          />
          <Route
            component={TalentOnboardContainerAmbient}
            path={`${match.url}/b`}
          />
        </Switch>
        {errorDialog}
      </div>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state) => ({
  talentProfile: state.talent_profile,
  uiState: state.uiState,
});

export default withGATracker(connect(mapStateToProps)(TalentOnboardContainer));
